import Head from "next/head";
import { Badge, Box, Card, Container, Divider, Group, Stack, Text, Title, useComputedColorScheme } from "@mantine/core";
import { SiteShell } from "../../components/SiteShell";
import { darkTheme, lightTheme } from "../../constants/theme";
import legalPrivacy from "../../constants/legalPrivacy";

function getSiteUrl(): string {
  const fromEnv = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  const fallback = process.env.NODE_ENV === "production" ? "https://legache.com" : "http://127.0.0.1:3001";
  const raw = fromEnv && fromEnv.length > 0 ? fromEnv : fallback;
  return raw.replace(/\/+$/, "");
}

function toAbsoluteUrl(pathname: string): string {
  return new URL(pathname, getSiteUrl()).toString();
}

export default function PrivacyPage() {
  const computedColorScheme = useComputedColorScheme("dark");
  const appliedTheme = computedColorScheme === "dark" ? darkTheme : lightTheme;
  const cardStyle = {
    background: appliedTheme.BACKGROUND_PRIMARY,
    borderColor: appliedTheme.BACKGROUND_MODIFIER_ACCENT,
  };

  return (
    <SiteShell>
      <Head>
        <title>{`${legalPrivacy.title} | Legache`}</title>
        <meta name="description" content={legalPrivacy.metaDescription} />
        <link rel="canonical" href={toAbsoluteUrl("/legal/privacy")} />
        <meta name="robots" content="index,follow" />
      </Head>

      <Box style={{ background: appliedTheme.TOOLBAR_BG, color: appliedTheme.TEXT_NORMAL }}>
        <Container size="lg" py={{ base: 52, sm: 72 }}>
          <Card withBorder radius="sm" padding="xl" shadow="sm" style={cardStyle}>
            <Stack gap="md">
              <Group gap="sm">
                <Badge variant="light" color="orange" radius="sm">
                  Legal
                </Badge>
              </Group>

              <Title order={1} fz={{ base: 32, sm: 40 }}>
                {legalPrivacy.title}
              </Title>
              <Text fz="lg" c="dimmed" lh={1.75}>
                {legalPrivacy.metaDescription}
              </Text>
              <Text fz="sm" c="dimmed">
                Last updated: {legalPrivacy.lastUpdated}
              </Text>
            </Stack>
          </Card>
        </Container>

        <Divider my={{ base: 28, sm: 36 }} />

        <Container size="lg" py={{ base: 20, sm: 28 }}>
          <Stack gap="lg">
            {Object.entries(legalPrivacy.sections).map(([sectionTitle, lines], idx, entries) => (
              <Box key={sectionTitle}>
                <Card withBorder radius="sm" padding="lg" shadow="sm" style={cardStyle}>
                  <Stack gap="sm">
                    <Title order={2} fz={{ base: 24, sm: 28 }}>
                      {sectionTitle}
                    </Title>
                    {lines.map((line, lineIdx) => (
                      <Text key={lineIdx} c="dimmed" lh={1.85} ml={line.startsWith("•") ? 15 : 0}>
                        {line}
                      </Text>
                    ))}
                  </Stack>
                </Card>
                {idx < entries.length - 1 ? <Divider my="lg" /> : null}
              </Box>
            ))}
          </Stack>
        </Container>

        <Divider my={{ base: 36, sm: 44 }} />
      </Box>
    </SiteShell>
  );
}
